import * as React from 'react';

import {
  WeightDisplayUnit,
  enumUnit,
  Unit,
} from '@rematter/frontend-lib-api-client';

import { LoadFormLineItem, WeightUnitType } from '~/modules/trade/types/load';

type Props = {
  weightDisplayUnitPreference?: WeightDisplayUnit | null;
  defaultWeightDisplayUnit: WeightUnitType;
  facilityDefaultUnit: Unit;
  onWeightDisplayUnitChange?: (unit: WeightDisplayUnit) => void;
};

export const useWeightUnit = ({
  weightDisplayUnitPreference,
  defaultWeightDisplayUnit,
  facilityDefaultUnit,
  onWeightDisplayUnitChange,
}: Props) => {
  // Helper function to compute weight unit from preference
  const getWeightUnitFromPreference = React.useCallback(
    (
      preference: WeightDisplayUnit | null | undefined,
      defaultUnit: WeightUnitType,
    ): WeightUnitType => {
      if (!preference) return defaultUnit;
      return preference === 'PriceUnit' ? 'price' : 'scale';
    },
    [],
  );

  // A toggle between scale and price unit
  const [weightUnit, setWeightUnit] = React.useState<WeightUnitType>(() =>
    getWeightUnitFromPreference(
      weightDisplayUnitPreference,
      defaultWeightDisplayUnit,
    ),
  );

  React.useEffect(() => {
    setWeightUnit(
      getWeightUnitFromPreference(
        weightDisplayUnitPreference,
        defaultWeightDisplayUnit,
      ),
    );
  }, [
    weightDisplayUnitPreference,
    defaultWeightDisplayUnit,
    getWeightUnitFromPreference,
  ]);

  // Handle weight unit changes
  const handleWeightUnitChange = React.useCallback(
    (newUnit: WeightUnitType) => {
      setWeightUnit(newUnit);
      if (onWeightDisplayUnitChange) {
        const mappedUnit: WeightDisplayUnit =
          newUnit === 'price' ? 'PriceUnit' : 'ScaleUnit';
        onWeightDisplayUnitChange(mappedUnit);
      }
    },
    [onWeightDisplayUnitChange],
  );

  // Determine display unit for footers based on toggle state
  const getFooterDisplayUnit = React.useCallback(
    (pricingUnit: Unit): Unit => {
      // Exception: "Each" always displays as "Each" regardless of toggle
      if (pricingUnit === enumUnit.Each) return enumUnit.Each;

      // If scale mode, use facility default; otherwise use pricing unit
      return weightUnit === 'scale' ? facilityDefaultUnit : pricingUnit;
    },
    [weightUnit, facilityDefaultUnit],
  );

  // Determine effective weight unit for individual line items
  // For "Each" items, ignore the toggle and use their stored unit
  const getLineItemWeightUnit = React.useCallback(
    (item: LoadFormLineItem): WeightUnitType => {
      // For "Each" items, don't apply the toggle - use stored unit
      if (item.pricingUnit === 'Each') {
        // Return 'price' to indicate using the item's own unit
        return 'price';
      }
      // For weight-based items, use the toggle value
      return weightUnit;
    },
    [weightUnit],
  );

  return {
    weightUnit,
    handleWeightUnitChange,
    getFooterDisplayUnit,
    getLineItemWeightUnit,
  };
};
